/**
 * Operating the platform (docs/plans/2026-10-cloud-licences-and-operations.md, section 2): what Mojo
 * Up's operators see of Mojo Up AI Cloud as a whole, and what the customer portal reads of it to
 * show beside a customer. The same shapes serve the console (`/operator/v1/…`, platform operators)
 * and the portal (`/internal/v1/operations…`, the shared integration key).
 *
 * The portal half is `packages/api/src/services/cloud-operations.ts` in mju-mojoupcustomerportal.
 * Change both sides together.
 */
import { z } from "zod";
import { cloudEntitlementSourceSchema, cloudSubscriptionStatusSchema } from "./billing.js";
import { cloudPlanSchema } from "./cloud.js";
import { statusComponentSchema, statusStateSchema } from "./status.js";
import { capacitySchema } from "./storage.js";
export const OPERATOR_OVERVIEW_PATH = "/operator/v1/overview";
export const OPERATOR_ORGANISATION_LIST_PATH = "/operator/v1/organisations";
export const INTERNAL_OPERATIONS_PATH = "/internal/v1/operations";
/** Where a team's plan comes from, for a list: a paying subscription, its free trial, a licence, or nothing. */
export const organisationPlanSourceSchema = z.enum(["subscription", "trial", "licence", "none"]);
/** One organisation as the operators' list shows it. */
export const operatorOrganisationRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    kind: z.enum(["enterprise", "personal", "team"]),
    plan: cloudPlanSchema,
    source: organisationPlanSourceSchema,
    /** The subscription's or licence's standing; `none` without either. */
    status: cloudSubscriptionStatusSchema,
    seatsPurchased: z.number().int().min(0),
    members: z.number().int().min(0),
    machines: z.number().int().min(0),
    /** Stored bytes at the last hourly count; absent before the first. */
    bytes: z.number().int().min(0).optional(),
    createdAt: z.string(),
    /** When a person or machine of the organisation was last seen; absent when none has been. */
    lastSeenAt: z.string().optional(),
    /** Its owners, so an operator finds an organisation from the address a customer writes from (Cloud only). */
    owners: z.array(z.object({ name: z.string().optional(), email: z.string().optional() })).optional(),
    /** The platform operator's own organisation: on Teams with no seat limit. */
    operator: z.boolean().optional(),
});
/** `GET /operator/v1/organisations`: every organisation, newest first. */
export const operatorOrganisationListSchema = z.object({
    /** When the rows were computed (they are cached for a minute). */
    computedAt: z.string(),
    organisations: z.array(operatorOrganisationRowSchema),
    /** With `q`, `limit`, `before` or a filter asked for: where the next page starts (absent when there is no more). */
    next: z.string().optional(),
    /** With a page asked for: how many organisations the platform holds, and how many match. */
    total: z.number().int().min(0).optional(),
    matching: z.number().int().min(0).optional(),
});
/** `GET /operator/v1/overview` and `GET /internal/v1/operations`: the platform as a whole. */
export const operatorOverviewSchema = z.object({
    computedAt: z.string(),
    edition: z.enum(["enterprise", "cloud"]),
    version: z.string(),
    /** The status page's last round: the same the public sees. */
    health: z.object({
        overall: statusStateSchema,
        checkedAt: z.string().optional(),
        components: z.array(statusComponentSchema),
        openIncidents: z.number().int().min(0),
    }),
    /** Load as the platform itself can see it; CPU, replicas and connections are the cloud provider's meters. */
    load: z.object({
        organisations: z.number().int().min(0),
        teams: z.number().int().min(0),
        personal: z.number().int().min(0),
        /** Organisations made in the last 24 hours. */
        organisationsToday: z.number().int().min(0),
        /** People with an account (Cloud); absent on Enterprise, where people come from the directory. */
        people: z.number().int().min(0).optional(),
        /** People seen in the last 24 hours (Cloud). */
        peopleSeenToday: z.number().int().min(0).optional(),
        machines: z.number().int().min(0),
        /** Machines that reported in the last hour. */
        machinesSeenThisHour: z.number().int().min(0),
        /** Teams on Teams, by what pays for it. */
        teamsOnTeams: z.object({ subscription: z.number().int().min(0), trial: z.number().int().min(0), licence: z.number().int().min(0) }),
    }),
    storage: z.object({
        /** Every organisation's stored bytes, as last counted. */
        organisationsBytes: z.number().int().min(0),
        /** The database's size on disk (Postgres); absent in memory. */
        databaseBytes: z.number().int().min(0).optional(),
        /** The disk the deployment gave the database; null when it did not say. */
        databaseAllocatedBytes: z.number().int().min(0).nullable(),
        measuredAt: z.string().optional(),
    }),
    capacity: capacitySchema,
});
/** `GET /internal/v1/operations/organisations/:tenantId`: one organisation for the portal's customer page. */
export const operationsOrganisationSchema = operatorOrganisationRowSchema.extend({
    billing: z.object({
        status: cloudSubscriptionStatusSchema,
        source: cloudEntitlementSourceSchema.or(z.literal("none")),
        seatsPurchased: z.number().int().min(0),
        seatLimit: z.number().int().min(0),
        openInvitations: z.number().int().min(0),
        trialEnd: z.string().optional(),
        periodEnd: z.string().optional(),
        licence: z.object({ id: z.string(), until: z.string(), ended: z.boolean() }).optional(),
        refreshedAt: z.string().optional(),
    }),
    owners: z.array(z.object({ userId: z.string(), name: z.string().optional(), email: z.string().optional() })),
});
// ── Removals ──────────────────────────────────────────────────────────────────
/**
 * Mojo Up's operators remove an AI Cloud organisation or person (abuse, a request under privacy
 * law, a customer gone). Cloud only, platform operators only, never the operators' own
 * organisation; each needs a reason and the name or email typed back. Everything the organisation
 * held goes (rows and objects); a person's sole organisations go with them. Written to the
 * operators' audit chain, and the people concerned are emailed with the reason.
 */
export const OPERATOR_REMOVALS_PATH = "/operator/v1/removals";
export const operatorRemoveOrganisationSchema = z.object({
    /** The organisation's id or slug. */
    organisationId: z.string().min(1).max(200),
    reason: z.string().trim().min(10).max(1000),
    /** The organisation's name, typed back. */
    confirm: z.string().min(1).max(200),
});
export const operatorRemovePersonSchema = z.object({
    /** The person's verified email address. */
    email: z.string().trim().toLowerCase().email(),
    reason: z.string().trim().min(10).max(1000),
    /** The email address, typed back. */
    confirm: z.string().min(1).max(320),
});
/** What a removal did. */
export const operatorRemovalResultSchema = z.object({
    kind: z.enum(["organisation", "person"]),
    /** The organisations purged (every row and object gone). */
    purged: z.array(z.object({ id: z.string(), name: z.string() })),
    /** For a person: the organisations they were taken out of but which carry on. */
    left: z.array(z.object({ id: z.string(), name: z.string() })),
    /** People emailed about it. */
    notified: z.number().int().min(0),
});
// ── Alerts from the customer portal → status incidents ────────────────────────
/**
 * Azure Monitor's alerts for Mojo Up's deployments go to the customer portal, which decides who is
 * told. For Mojo Up AI Cloud it also opens a public status incident here: a Sev0 as soon as it
 * fires (impact `outage`), a Sev1 once it has been firing for ten minutes (impact `major`), and
 * resolves it when the alert clears. The push carries the entitlement push's HMAC headers and is
 * idempotent on `alertId`. Rule names are internal, so the portal sends the component and Cloud
 * writes the public wording.
 */
export const STATUS_ALERTS_PATH = "/internal/v1/status/incidents";
export const alertComponentSchema = z.enum(["api", "console", "database", "cache", "other"]);
export const alertIncidentOpenSchema = z.object({
    alertId: z.string().min(1).max(200),
    severity: z.union([z.literal(0), z.literal(1)]),
    component: alertComponentSchema,
    impact: z.enum(["major", "outage"]),
    startedAt: z.string().refine((s) => !Number.isNaN(Date.parse(s)), "a date and time (ISO 8601)"),
});
export const alertIncidentResolveSchema = z.object({
    alertId: z.string().min(1).max(200),
    resolvedAt: z.string().refine((s) => !Number.isNaN(Date.parse(s)), "a date and time (ISO 8601)"),
});
/** The answer: the incident the alert maps to, and what the push did. */
export const alertIncidentResultSchema = z.object({
    incidentId: z.string(),
    result: z.enum(["opened", "already_open", "resolved", "already_resolved"]),
});
//# sourceMappingURL=operations.js.map